/* eslint-disable no-shadow */
/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { customAlphabet } from 'nanoid';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Combobox } from '@headlessui/react';

/**
 * This custom dropdown is a React component displayed as editor of the jspreadsheet-ce's cell. It uses tailwind css and accepts values from outside of the list.
 *
 * @param {string} initialValue - The initial value of the dropdown
 * @param {function} onSave - The function that saves the selected value
 * @param {array} source - The array to be displayed in the dropdown; expects normalized array of { id, name }
 * @returns a React component
 *
 */
export default function CustomDropdown({ initialValue, onSave, source }) {
  const { t } = useTranslation('accountDetails');

  const initialId = useMemo(() => (typeof initialValue === 'string' ? initialValue : initialValue?.id || ''), [initialValue]);
  const [selectedId, setSelectedId] = useState(initialId);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setSelectedId(initialId);
  }, [initialId]);

  const options = source;
  const optionLabelMap = useMemo(
    () => options.reduce((acc, option) => {
      acc[option.id] = option.name || option.id;
      return acc;
    }, {}),
    [options],
  );

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const lowered = query.toLowerCase();
    return options.filter((option) => {
      const label = option.name || option.id;
      return label.toLowerCase().includes(lowered) || option.id.toLowerCase().includes(lowered);
    });
  }, [options, query]);

  function handleInput(e) {
    setQuery(e.target.value);
  }

  // after initialisation immediately set focus to the input component to emulate the behaviour of the jspreadsheet
  const myIdRef = useRef(customAlphabet('qwertzuioplkjhgfdsayxcvbnmQWERTZUIOPLKJHGFDSAYXCVBNM', 3)());
  useEffect(() => {
    document.querySelector(`#${myIdRef.current}`).focus();
  }, []);

  return (
    <div className="">
      <Combobox
        value={selectedId}
        onChange={(value) => {
          if (value == null) return;
          const nextValue = typeof value === 'string' ? value : value.id;
          setSelectedId(nextValue);
          onSave(nextValue);
        }}
      >
        <Combobox.Input
          id={myIdRef.current}
          displayValue={(value) => optionLabelMap[value] || value || ''}
          className="w-full p-0 text-sm text-gray-700 bg-white border-0 appearance-none focus:outline-none focus:shadow-outline"
          onChange={handleInput}
        />
        <Combobox.Options static className="absolute z-[999] mt-2 overflow-auto text-xs bg-white shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {query !== '' && !source.map((item) => item.id).includes(query) && (
            <Combobox.Option value={query}>
              {({ active }) => <div key="custom" className={`cursor-default select-none relative py-1 px-2 ${active ? 'text-white bg-blue-600' : 'text-gray-900'}`}>{`${t('create')} ${query}`}</div>}
            </Combobox.Option>
          )}
          {filteredOptions.map((option) => (
            <Combobox.Option key={option.id} value={option.id} className={({ active }) => `cursor-default select-none relative py-1 px-2 ${active ? 'text-white bg-blue-600' : 'text-gray-900'}`}>
              {({ selected, active }) => (
                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                  <span>{option.name || option.id || option}</span>
                </span>
              )}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </Combobox>
    </div>
  );
}
CustomDropdown.propTypes = {
  initialValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ]),
  onSave: PropTypes.func.isRequired,
  source: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
};
CustomDropdown.defaultProps = {
  initialValue: '',
};
